'use client';

import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import { CONVERSATION_DETAIL_POLL_MS } from '../lib/pollingConstants';
import { resolvePollIntervalMs, type PollingEndpointKey } from '../lib/pollingManager';
import { usePollingGate } from './usePollingGate';

const ENDPOINT_KEY: PollingEndpointKey = 'conversation-detail';

type Options = {
  conversationId: number | null | undefined;
  enabled?: boolean;
  poll?: boolean;
  messageLimit?: number;
};

export function useConversationDetailQuery(options: Options) {
  const {
    conversationId,
    enabled = true,
    poll = false,
    messageLimit = 100,
  } = options;
  const { allowPolling } = usePollingGate();
  const id = conversationId ?? null;
  const isValidId = id != null && !Number.isNaN(id) && id > 0;

  return useQuery({
    queryKey: ['conversation', id],
    queryFn: () => apiService.getConversation(id!, 1, messageLimit),
    enabled: enabled && isValidId,
    retry: 0,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchInterval: resolvePollIntervalMs(
      poll && isValidId ? CONVERSATION_DETAIL_POLL_MS : false,
      ENDPOINT_KEY,
      allowPolling,
    ),
    meta: { pollingEndpoint: ENDPOINT_KEY },
  });
}
